"use client"

import Image from "next/image"
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import { useDefaultProfileImages } from "@/lib/default-profile-images"
import type { EarnedBadge } from "@/lib/badges/types"

interface AvatarWithBadgeProps {
  src: string | null | undefined
  alt?: string
  /** Diameter of the avatar in px */
  size?: number
  className?: string
  /** Pass pre-fetched badges to avoid an extra fetch */
  badges?: EarnedBadge[]
  /** Pass userId to auto-fetch badges (ignored if `badges` prop is set) */
  userId?: string
  /** If true, skip badge display entirely (e.g. in dense list rows) */
  noBadge?: boolean
}

/** Priority order for which badge shows as the ring — higher index = higher priority. */
const BADGE_PRIORITY = [
  "founder",
  "goongod",
  "artgod", "scriptgod", "sonicgod", "vagod", "editgod",
  "omnicreator", "gamegod",
]

function pickFeaturedBadge(badges: EarnedBadge[]): EarnedBadge | null {
  let best: EarnedBadge | null = null
  let bestScore = -1
  for (const b of badges) {
    const priorityIdx = BADGE_PRIORITY.indexOf(b.slug)
    // Score: priority index * 10 + rank (so rank 3 of a lower-priority badge
    // still loses to rank 1 of a higher-priority one)
    const score = priorityIdx >= 0
      ? priorityIdx * 10 + b.rank
      : b.rank  // non-listed badges rank by their earned rank only
    if (score > bestScore) { bestScore = score; best = b }
  }
  return best
}

export function AvatarWithBadge({
  src,
  alt = "",
  size = 32,
  className,
  badges: badgesProp,
  userId,
  noBadge = false,
}: AvatarWithBadgeProps) {
  const [fetchedBadges, setFetchedBadges] = useState<EarnedBadge[] | null>(null)
  const { avatarUrl: defaultAvatarUrl } = useDefaultProfileImages()

  useEffect(() => {
    if (noBadge || badgesProp !== undefined || !userId) return
    fetch(`/api/badges/${userId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data?.badges) setFetchedBadges(data.badges) })
      .catch(() => {})
  }, [userId, badgesProp, noBadge])

  const badges = badgesProp ?? fetchedBadges ?? []
  const featured = noBadge ? null : pickFeaturedBadge(badges)

  const badgeSize = Math.round(size * 0.45)
  const offset = Math.round(badgeSize * 0.15)

  return (
    <div
      className={cn("relative shrink-0 inline-flex", className)}
      style={{ width: size, height: size }}
    >
      {/* Avatar */}
      <div
        className="w-full h-full rounded-full overflow-hidden bg-muted"
        style={{ width: size, height: size }}
      >
        <Image
          src={src || defaultAvatarUrl}
          alt={alt}
          width={size}
          height={size}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Badge ring overlay — bottom-right corner */}
      {featured && (
        <div
          className="absolute rounded-full ring-2 ring-background overflow-hidden"
          style={{
            width: badgeSize,
            height: badgeSize,
            bottom: -offset,
            right: -offset,
          }}
        >
          <Image
            src={`/badges/${featured.slug}_r${featured.rank}.webp`}
            alt={featured.name}
            width={badgeSize}
            height={badgeSize}
            className="w-full h-full object-contain"
          />
        </div>
      )}
    </div>
  )
}
